/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { BarChartProps, BarItemData } from './barChart.types';
import { getBarGapRatio } from './utils/barSeriesUtils';
import { useUser } from '#hooks/UNSAFE_useUser';
import { BarGroup } from './BarGroup';
import {
  findNearestDataPoint,
  getStartAndEndAxesOffset
} from '#utils/PRIVATE_chartUtils/layoutUtils';
import {
  getBarCenterCoordinate,
  getBarPosition,
  getSeriesOffsetAndWidth
} from './utils/barLayoutUtils';
import { getBarChartNavUtil } from './utils/barNavUtils';
import { Chart } from '#PRIVATE_Chart';
import { TimeAxisProps, Scale, SizeHandle } from '#utils/UNSAFE_visTypes/chart';
import { getItemColor } from '#utils/PRIVATE_chartUtils/dataStyleUtils';
import { OverviewBarChart } from './OverviewBarChart';
import { GroupLevelItem } from '#PRIVATE_Axis/groupAxis/hierarchicalUtils';

import { forwardRef, Ref } from 'preact/compat';
import { VNode } from 'preact';

export type ForwardRefBarChart = <K extends string | number, D extends BarItemData<K>>(
  props: BarChartProps<K, D> & {
    ref?: Ref<SizeHandle>;
  }
) => VNode | null;

/**
 * A Bar chart displays information graphically using rectangular bars with heights or lengths proportional to the values that they represent,
 * making relationships among the data easier to understand.
 */
export const BarChart = forwardRef(
  <K extends string | number, D extends BarItemData<K>>(
    {
  width,
  height,
  selectedIds = [],
  hiddenIds = [],
  highlightedIds,
  hideAndShowBehavior = 'none',
  orientation = 'vertical',
  yAxis,
  xAxis,
  y2Axis,
  series,
  groups,
  stacking = 'off',
  getDataItem,
  selectionMode = 'none',
  drilling = 'off',
  dataItemGaps = 0.5,
  ...props
}: BarChartProps<K, D>,
    ref: Ref<SizeHandle>
  ) => {
  const { direction } = useUser();
  const isRtl = direction === 'rtl';
  const hiddenSet = new Set(hiddenIds);
  const isHoriz = orientation === 'horizontal';
  const timeAxisType = (xAxis as TimeAxisProps)?.timeAxisType;
  const selectedSet = new Set(selectedIds);
  const highlightedSet = new Set(highlightedIds);

  const isStacked = stacking != 'off';
  const stackLabelStyle = typeof stacking === 'string' ? undefined : stacking.stackLabelStyle;
  const getStackGroup = typeof stacking != 'string' ? stacking.getStackGroup : () => 'default';
  const getStackLabel =
    typeof stacking === 'object' && stacking.getStackLabel ? stacking.getStackLabel : undefined;

  const getGapRatio = (startIndex: number, endIndex: number) => {
    return getBarGapRatio(isStacked, startIndex, endIndex);
  };
  const getNextChartItemFunc = (startIndex: number, endIndex: number) => {
    const stackGroups = isStacked
      ? Array.from(new Set(series.map((s) => getStackGroup(s) || 'default')))
      : [];
    return getBarChartNavUtil(
      getDataItem,
      series,
      series.length,
      startIndex,
      endIndex,
      isStacked,
      isRtl,
      isHoriz,
      stackGroups,
      getStackGroup
    );
  };
  const isSplitDualY = typeof y2Axis?.split === 'number';
  const getDataItemPos = (
    xScale: Scale,
    yScale: Scale,
    y2Scale: Scale,
    axisStepWidth: number,
    averageGroupZ: number,
    gapRatio: number
  ) => {
    return getBarPosition(
      series,
      groups,
      getDataItem,
      xScale,
      yScale,
      y2Scale,
      hiddenSet,
      dataItemGaps,
      axisStepWidth,
      averageGroupZ,
      gapRatio,
      yAxis?.scale === 'log',
      isStacked,
      isRtl,
      isHoriz,
      isSplitDualY,
      getStackGroup,
      timeAxisType
    );
  };

  const findNearest = (
    xScale: Scale,
    yScale: Scale,
    y2Scale: Scale,
    axisStepWidth: number,
    averageGroupZ: number,
    gapRatio: number
  ) => {
    const getSeriesDataInfo = getSeriesOffsetAndWidth(
      axisStepWidth,
      averageGroupZ,
      series,
      getDataItem,
      gapRatio,
      dataItemGaps,
      isStacked,
      isRtl,
      isHoriz
    );
    return findNearestDataPoint(
      series,
      groups,
      xScale,
      yScale,
      getDataItem,
      isStacked,
      getSeriesDataInfo,
      getBarCenterCoordinate,
      yAxis?.scale === 'log',
      dataItemGaps,
      hiddenSet,
      timeAxisType,
      y2Scale
    );
  };

  const getMarkerColorAndType = (seriesIndex: number, groupIndex: number) => {
    return {
      color: getItemColor(seriesIndex, groupIndex, series, getDataItem),
      type: 'circle' as const
    };
  };

  const getOffset = (groupCount: number) => {
    return getStartAndEndAxesOffset(false, false, isHoriz, groupCount, true);
  };

  return (
    <Chart
      ref={ref}
      width={width}
      height={height}
      yAxis={yAxis}
      xAxis={xAxis}
      y2Axis={y2Axis}
      selectedIds={selectedSet}
      hiddenIds={hiddenIds}
      hideAndShowBehavior={hideAndShowBehavior}
      orientation={orientation}
      series={series}
      groups={groups}
      isStacked={isStacked}
      getDataItem={getDataItem}
      stackLabelStyle={stackLabelStyle}
      selectionMode={selectionMode}
      getStackGroup={getStackGroup}
      drilling={drilling}
      renderGridLinesInFront={false}
      findNearest={findNearest}
      getDataItemPos={getDataItemPos}
      getNavUtil={getNextChartItemFunc}
      getGapRatio={getGapRatio}
      getMarkerInfo={getMarkerColorAndType}
      isRtl={isRtl}
      getStartAndEndAxesOffset={getOffset}
      defaultOverviewContentRenderer={() => {
        return <OverviewBarChart series={series} groups={groups} getDataItem={getDataItem} />;
      }}
      {...props}>
      {({
        groupsInfo,
        xStartIndex,
        xEndIndex,
        xScale,
        yScale,
        y2Scale,
        gapRatio,
        activeId,
        focusedItemInfo,
        hoveredItemInfo,
        axisStepWidth,
        averageGroupZ,
        isPointInsideMarquee,
        getTextDimensions,
        defaultFontSize,
        resolvedColors,
        getItemAriaLabel,
        plotAreaClipPathId
      }) => {
        return (
          <g clipPath={`url(#${plotAreaClipPathId})`}>
            {groupsInfo
              .slice(xStartIndex, xEndIndex + 1)
              .map(({ item: group }: GroupLevelItem, index: number) => (
                <BarGroup
                  yScale={yScale}
                  splitDualY={isSplitDualY}
                  y2Scale={y2Scale}
                  xScale={xScale}
                  groupIndex={index + xStartIndex}
                  selectedIds={selectedSet}
                  hiddenIds={hiddenSet}
                  highlightedIds={highlightedIds ? highlightedSet : highlightedIds}
                  isDrillEnabled={drilling === 'on'}
                  series={series}
                  barGapRatio={gapRatio}
                  dataItemGaps={dataItemGaps}
                  isPointInsideMarquee={isPointInsideMarquee}
                  isStacked={isStacked}
                  axisStepWidth={axisStepWidth}
                  averageGroupZ={averageGroupZ!}
                  group={group}
                  timeAxisType={(xAxis as TimeAxisProps)?.timeAxisType}
                  colors={resolvedColors}
                  activeId={activeId}
                  isLog={yAxis?.scale === 'log'}
                  orientation={orientation}
                  getDataItem={getDataItem}
                  isSelectionEnabled={selectionMode != 'none'}
                  isRtl={isRtl}
                  focusedItemInfo={focusedItemInfo}
                  hoveredItemInfo={hoveredItemInfo}
                  getTextDimensions={getTextDimensions}
                  defaultFontSize={defaultFontSize}
                  getStackGroup={getStackGroup}
                  getStackLabel={getStackLabel}
                  getItemAriaLabel={getItemAriaLabel}
                />
              ))}
          </g>
        );
      }}
    </Chart>
  );
}) as ForwardRefBarChart;
